import React, { createContext, FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useRecordingStateManager } from 'hooks/useRecordingStateManager/useRecordingStateManager';
import { useDataFetcher } from '../../hooks/useDataFetcher/useDataFetcher';
import { FetchParamById } from '../../types/ApiTypes';
import GSApi from '../../api/Api';
import { useGlobalNetworkError, useGlobalNoInternet } from '../../hooks/withNetworkCheck/withNetworkCheck';
import Events from '../../utils/Events';
import { RecordingEvent, RecordingRef, TimeClusterRecordingTypeMap } from '../../types/RecordingTypes';
import { AlertDialog } from '../../components/AlertDialog/AlertDialog';
import translate from '../../utils/fnTranslate';
import { useRecordingNotification } from '../../components/Notification/Notification';
import { TimeCluster } from '../../hooks/useBroadcastTimeCluster/useBroadcastTimeCluster';
import { useSubscription } from '../useSubscription/SubscriptionContext';
import { useAuth } from '../useAuth/AuthContext';
import { LoadingIndicator } from '../../components/LoadingIndicator/LoadingIndicator';
import { hexToRgba } from '../../utils/fnColors';
import { useConfig } from '../useConfig/ConfigContext';
import { StaticPageRoutes } from '../../types/RouteTypes';

const ERROR_CODE_STORAGE_FULL = 'ERecording.QuotaExceeded';
const ERROR_CODE_RECORDING_LIMIT_REACHED = 'recording_limit_reached';

export const CHANNELS_WITHOUT_NPVR = [
    '53027', // Duo One
];

const useRecordingService = () => {
    const { onNoInternet } = useGlobalNoInternet();
    const { onNetworkError } = useGlobalNetworkError();
    const recordingNotification = useRecordingNotification();
    const isLoadingRef = useRef(false);
    const { initSubscribeFlow } = useSubscription();
    const { refreshAccountInfo } = useAuth();

    const eventEmitter = useRef<Events>(new Events());

    const [toDeleteFromEPG, setToDeleteFromEPG] = useState<any>(null);
    const [toDelete, setToDelete] = useState<RecordingRef>(null);
    const [toAdd, setToAdd] = useState<RecordingRef>(null);
    const [error, setError] = useState<any>(null);
    const [toDeleteAll, setToDeleteAll] = useState<boolean>(false);
    const [isRecorded, setIsRecorded] = useState(false);
    const [deleteQueue, setDeleteQueue] = useState([]);
    const skipNotificationRef = useRef(false);
    const deletedRecordingRef = useRef<RecordingRef>(null);
    const { setIsCurrentlyRecording, isCurrentlyRecording } = useRecordingStateManager();
    const recordRef = useRef<(id: string) => Promise<any>>(null);

    const {
        response: createRecordingsResponse,
        error: createRecordingError,
        loading: isRecording,
        fetcher: record,
        reset: resetRecordingApi,
    } = useDataFetcher<any, string>((id) => GSApi.createRecordings(id), onNoInternet, onNetworkError);

    // Keep ref updated with latest record function
    recordRef.current = record;

    const {
        response: recordingsResponse,
        error: fetchRecordingsError,
        loading: recordingDetailsLoading,
        fetcher: getRecordings,
        hasLoaded: recordingsReady,
    } = useDataFetcher<any, void>(() => GSApi.getRecordings(), onNoInternet, onNetworkError);

    const {
        response: deleteRecordingResponse,
        error: deleteRecordingError,
        loading: isDeletingRecording,
        fetcher: deleteRecordingRequest,
        reset: resetDeleteRecordingApi,
    } = useDataFetcher<any, FetchParamById>((param) => GSApi.deleteRecording(param.id), onNoInternet, onNetworkError);

    const {
        response: deleteAllRecordingsResponse,
        error: deleteAllRecordingsError,
        loading: isDeletingAllrecordings,
        fetcher: deleteAllRecordingsRequest,
    } = useDataFetcher<any, void>(() => GSApi.deleteAllRecordings(), onNoInternet, onNetworkError);

    const deleteAllRecordings = async () => {
        const result = await deleteAllRecordingsRequest(null);

        await getRecordings();

        eventEmitter.current.triggerEvents(RecordingEvent.MANAGE, null);

        return result;
    };

    const reset = () => {
        resetRecordingApi();
        resetDeleteRecordingApi();
    };

    const addEventListener = (event: RecordingEvent, callback) => {
        eventEmitter.current.addEventListener(event, callback);
    };

    const removeEventListener = (event: RecordingEvent, callback) => {
        eventEmitter.current.removeEventListener(event, callback);
    };

    const checkIsRecording = useCallback((assetId) => isCurrentlyRecording(assetId), [isCurrentlyRecording]);

    const updateIsRecorded = (updatedIsRecording) => {
        setIsRecorded(updatedIsRecording);
    };

    // Check for existing recording of the same program (EG: Telehommik has the same program in different times)
    const findExistingRecordingByProgram = useCallback(
        (channelId: string, programSeriesId: string) => {
            if (!recordingsResponse?.results || !channelId || !programSeriesId) {
                return null;
            }

            return recordingsResponse.results.find(
                (recording) => recording.listing.channel_id === channelId && recording.listing.program.program_series_id === programSeriesId
            );
        },
        [recordingsResponse]
    );

    const fetchRecordings = () => {
        if (isLoadingRef.current || recordingDetailsLoading) return;
        if (recordingsResponse) return;
        isLoadingRef.current = true;

        getRecordings().finally(() => {
            isLoadingRef.current = false;
        });
    };

    const findRecordingInResults = useCallback((results: any[], recordingId: string, channelId?: string, programSeriesId?: string) => {
        if (!results) return null;

        if (channelId && programSeriesId) {
            const match = results.find(
                (recording) => recording.listing.channel_id === channelId && recording.listing.program.program_series_id === programSeriesId
            );
            if (match) return match;
        }

        return results.find((recording) => recording.listing.id === recordingId) || null;
    }, []);

    const deleteRecording = useCallback(
        async (recordingId, timeCluster, channelId, programSeriesId, skipNotification = false) => {
            let existingRecording = findRecordingInResults(recordingsResponse?.results, recordingId, channelId, programSeriesId);

            // If not found, the local recordingsResponse may be stale — re-fetch and retry
            if (!existingRecording) {
                const freshResponse = await getRecordings();
                const freshResults = freshResponse?.response?.results;
                existingRecording = findRecordingInResults(freshResults, recordingId, channelId, programSeriesId);
            }

            if (existingRecording) {
                if (skipNotification) {
                    deleteRecordingRequest({ id: existingRecording.id });
                    updateIsRecorded(false);
                    skipNotificationRef.current = true;
                    setToDeleteFromEPG(existingRecording.listing.id);
                } else {
                    const deleteRef = {
                        id: existingRecording.id,
                        recordingId: existingRecording.listing.id,
                        type: TimeClusterRecordingTypeMap[timeCluster] || 'RECORDED',
                        skipNotification,
                    };
                    setToDelete(deleteRef);
                }
            } else {
                console.error(`Recording ${recordingId} was not found`);
            }
        },
        [recordingsResponse, findRecordingInResults, getRecordings, deleteRecordingRequest, updateIsRecorded]
    );

    const createRecordings = async (
        id: string,
        timeCluster: TimeCluster,
        channelId?: string,
        programSeriesId?: string,
        skipNotification?: boolean,
        assetId?: string
    ) => {
        if (channelId && programSeriesId) {
            const existingRecording = findExistingRecordingByProgram(channelId, programSeriesId);
            if (existingRecording) {
                setIsRecorded(true);
                return;
            }
        }

        const recordingStateId = assetId || id;
        setIsCurrentlyRecording(recordingStateId, true);

        setToAdd({
            id,
            assetId: recordingStateId,
            type: TimeClusterRecordingTypeMap[timeCluster],
            skipNotification,
        });
    };

    const initRecordingUpgradeFlow = (listingId: string, onSuccess?: () => void) => {
        initSubscribeFlow(
            null,
            {
                code: listingId,
                type: 'Listing',
                capability: 'RECORDING',
            },
            {
                onSuccess: () => {
                    // Refresh account info to get updated service_codes
                    refreshAccountInfo();
                    eventEmitter.current.triggerEvents(RecordingEvent.MANAGE, null);
                    onSuccess?.();
                },
            }
        );
    };

    const processDeleteQueue = useCallback(() => {
        if (deleteQueue.length > 0 && recordingsResponse) {
            const { recordingId, timeCluster, channelId, programSeriesId, skipNotification = false } = deleteQueue[0];

            deleteRecording(recordingId, timeCluster, channelId || null, programSeriesId || null, skipNotification);

            setDeleteQueue((prevQueue) => prevQueue.slice(1));
        }
    }, [deleteQueue, recordingsResponse, deleteRecording]);

    useEffect(() => {
        if (recordingsResponse) {
            processDeleteQueue();
        }
    }, [recordingsResponse, processDeleteQueue]);

    useEffect(() => {
        if (createRecordingError) {
            const errorCode = createRecordingError?.response?.data?.errors?.[0]?.code;

            // Reset the recording state since the API call failed
            if (toAdd?.assetId) {
                setIsCurrentlyRecording(toAdd.assetId, false);
                updateIsRecorded(false);
            }

            if (errorCode === ERROR_CODE_RECORDING_LIMIT_REACHED) {
                setError(createRecordingError);
                setToAdd(null);
                return;
            }

            if (errorCode !== ERROR_CODE_STORAGE_FULL) {
                recordingNotification(
                    toAdd.type,
                    'ADD',
                    () => {
                        setToAdd(null);
                    },
                    true
                );
            } else {
                setError(createRecordingError);
            }
        }
    }, [createRecordingError]);

    useEffect(() => {
        if (createRecordingsResponse) {
            updateIsRecorded(true);
            getRecordings();

            if (!toAdd.skipNotification) {
                recordingNotification(toAdd.type, 'ADD', () => {
                    eventEmitter.current.triggerEvents(RecordingEvent.ADD, toAdd.id);
                    eventEmitter.current.triggerEvents(RecordingEvent.MANAGE, toAdd.id);
                    setToAdd(null);
                });
            } else {
                eventEmitter.current.triggerEvents(RecordingEvent.ADD, toAdd.id);
                eventEmitter.current.triggerEvents(RecordingEvent.MANAGE, toAdd.id);
                setToAdd(null);
            }
        }
    }, [createRecordingsResponse]);

    useEffect(() => {
        if (deleteRecordingResponse !== null && deleteRecordingResponse !== undefined && !deleteRecordingError) {
            const deleted = toDelete || deletedRecordingRef.current;

            if (deleted) {
                if (!deleted.skipNotification && !skipNotificationRef.current) {
                    recordingNotification(deleted.type, 'REMOVE', () => {
                        eventEmitter.current.triggerEvents(RecordingEvent.DELETE, deleted.id);
                        eventEmitter.current.triggerEvents(RecordingEvent.MANAGE, deleted.id);

                        setToDelete(null);
                        setToAdd(null);
                        setIsCurrentlyRecording(deleted.recordingId, false);
                    });
                    getRecordings();
                } else {
                    eventEmitter.current.triggerEvents(RecordingEvent.DELETE, deleted.id);
                    eventEmitter.current.triggerEvents(RecordingEvent.MANAGE, deleted.id);

                    setToDelete(null);
                    getRecordings();
                    setIsCurrentlyRecording(deleted.recordingId, false);
                }
            } else {
                setIsCurrentlyRecording(toDeleteFromEPG, false);
                updateIsRecorded(false);
                getRecordings();
            }

            deletedRecordingRef.current = null;
            skipNotificationRef.current = false;
        }
    }, [deleteRecordingResponse, deleteRecordingError]);

    useEffect(() => {
        setError(deleteAllRecordingsError);
    }, [deleteAllRecordingsError]);

    useEffect(() => {
        if (toAdd && recordRef.current) {
            recordRef.current(toAdd.id);
        }
    }, [toAdd]);

    return {
        createRecordingsResponse,
        isRecording,
        record,
        getRecordings: fetchRecordings,
        recordingDetailsLoading,
        recordingsResponse,
        recordingsReady,
        setIsCurrentlyRecording,
        fetchRecordingsError,
        deleteRecordingResponse,
        isDeletingRecording,
        deleteRecordingRequest,
        updateIsRecorded,
        isRecorded,
        findExistingRecordingByProgram,
        deleteAllRecordingsResponse,
        deleteAllRecordingsRequest,
        deleteAllRecordings,
        isDeletingAllrecordings,
        toDelete,
        toDeleteAll,
        setToDeleteAll,
        setToDelete,
        deletedRecordingRef,
        deleteRecording,
        createRecordings,
        createRecordingError,
        deleteRecordingError,
        error,
        setError,
        reset,
        addEventListener,
        removeEventListener,
        checkIsRecording,
        initRecordingUpgradeFlow,
    };
};

export const RecordingContext = createContext<ReturnType<typeof useRecordingService>>(null);

export const RecordingProvider = ({ children }) => {
    const service = useRecordingService();

    return <RecordingContext.Provider value={service}>{children}</RecordingContext.Provider>;
};

export const useRecording = () => useContext(RecordingContext);

export const DeleteRecordingConfirmation: FC = () => {
    const { toDelete, setToDelete, deleteRecordingRequest, updateIsRecorded, deletedRecordingRef } = useRecording();

    if (!toDelete || toDelete.skipNotification) return null;

    const getTitle = () => {
        switch (toDelete?.type) {
            case 'RECORDED':
                return translate('ALERT_DELETE_RECORDING_TITLE');
            case 'ONGOING':
                return translate('ALERT_STOP_RECORDING_TITLE');
            case 'SCHEDULED':
                return translate('ALERT_CANCEL_RECORDING_TITLE');
            default:
                return null;
        }
    };

    const getMessage = () => {
        switch (toDelete?.type) {
            case 'RECORDED':
                return translate('ALERT_DELETE_RECORDING_BODY');
            case 'ONGOING':
                return translate('ALERT_STOP_RECORDING_BODY');
            case 'SCHEDULED':
                return translate('ALERT_CANCEL_RECORDING_BODY');
            default:
                return null;
        }
    };

    const idToRemove = toDelete?.id;
    const deleteMethod = deleteRecordingRequest;

    return (
        <AlertDialog
            title={getTitle()}
            bodyText={getMessage()}
            buttons={[
                {
                    text: translate('BUTTON_NO'),
                    onClick: () => setToDelete(null),
                },
                {
                    text: translate('BUTTON_YES'),
                    onClick: async () => {
                        deletedRecordingRef.current = toDelete;
                        setToDelete(null);
                        await deleteMethod({ id: idToRemove });
                        await updateIsRecorded(false);
                    },
                },
            ]}
        />
    );
};

export const ManageRecordingLoadingOverlay = () => {
    const { isDeletingAllrecordings } = useRecording();
    const { config } = useConfig();

    return (
        <LoadingIndicator
            overlay={true}
            isLoading={isDeletingAllrecordings}
            backgroundOverwrite={hexToRgba(config.layout_config.black_color, 0.8)}
            loaderHint={isDeletingAllrecordings && translate('DELETE_ALL_RECORDINGS_HINT')}
        />
    );
};

export const CreateRecordingError = () => {
    const { error, setError } = useRecording();
    const navigate = useNavigate();

    if (error) {
        const errorCode = error?.response?.data?.errors?.[0]?.code;
        const isStorageFullError = errorCode === ERROR_CODE_STORAGE_FULL || errorCode === ERROR_CODE_RECORDING_LIMIT_REACHED;
        const title = isStorageFullError ? 'ERR_RECORDING_STORAGE_FULL_TITLE' : 'ERR_GENERIC_TITLE';
        const bodyText = isStorageFullError ? 'ERR_RECORDING_STORAGE_FULL_BODY' : 'ERR_GENERIC_BODY';

        return (
            <AlertDialog
                title={translate(title)}
                bodyText={translate(bodyText)}
                buttons={[
                    {
                        text: translate('CLOSE_BUTTON'),
                        onClick: () => setError(null),
                    },
                    {
                        text: translate('MANAGE_RECORDINGS_BUTTON'),
                        onClick: () => {
                            navigate(StaticPageRoutes.RECORDINGS_SETTINGS);
                            setError(null);
                        },
                    },
                ]}
            />
        );
    }

    return null;
};
